import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type {
  SkillGapResult,
  TenderPersonaResult,
  PersonaMatchResult,
  EmployeePersonaGapResult,
  Tender,
} from '../types';
import type { EmployeeListItem } from '../api/employees';
import {
  fetchSkillGaps,
  fetchTenderPersonas,
  fetchPersonaMatches,
  fetchEmployeePersonaGaps,
} from '../api/analytics';
import { fetchTenders } from '../api/tenders';
import { fetchEmployees } from '../api/employees';
import SkillBadge from '../components/SkillBadge';
import PersonaCard from '../components/PersonaCard';

// -------------------------------------------------------------------------
// Tab definitions
// -------------------------------------------------------------------------

type TabKey = 'skill-gaps' | 'tender-personas' | 'persona-matches' | 'employee-gaps';

const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'skill-gaps', labelKey: 'analytics.tabSkillGaps' },
  { key: 'tender-personas', labelKey: 'analytics.tabTenderPersonas' },
  { key: 'persona-matches', labelKey: 'analytics.tabPersonaMatches' },
  { key: 'employee-gaps', labelKey: 'analytics.tabEmployeeGaps' },
];

// -------------------------------------------------------------------------
// Main component
// -------------------------------------------------------------------------

export default function Analytics() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('skill-gaps');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('analytics.title', 'Analytics')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('analytics.subtitle', 'Skill gap analysis, persona matching, and employee development insights.')}
        </p>
      </div>

      {/* Tab bar */}
      <nav aria-label={t('analytics.tabNavigation', 'Analytics sections')} className="border-b border-gray-200">
        <div className="flex gap-4 -mb-px" role="tablist">
          {TABS.map(({ key, labelKey }) => (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={activeTab === key}
              onClick={() => setActiveTab(key)}
              className={[
                'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              ].join(' ')}
            >
              {t(labelKey, key)}
            </button>
          ))}
        </div>
      </nav>

      {/* Tab panels */}
      {activeTab === 'skill-gaps' && <SkillGapsTab />}
      {activeTab === 'tender-personas' && <TenderPersonasTab />}
      {activeTab === 'persona-matches' && <PersonaMatchesTab />}
      {activeTab === 'employee-gaps' && <EmployeeGapsTab />}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 1: Skill Gaps
// -------------------------------------------------------------------------

function SkillGapsTab() {
  const { t } = useTranslation();
  const [data, setData] = useState<SkillGapResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gapsOnly, setGapsOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSkillGaps()
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!data || data.gaps.length === 0) return <EmptyState message={t('analytics.noSkillGaps', 'No skill gap data available.')} />;

  const displayed = gapsOnly ? data.gaps.filter((g) => !g.any_employee_has) : data.gaps;

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card">
          <p className="text-sm text-gray-500">{t('analytics.skillsDemanded', 'Skills Demanded')}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{data.total_tender_skills_demanded}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">{t('analytics.companySkills', 'Company Skills')}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{data.total_company_skills}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">{t('analytics.skillGaps', 'Skill Gaps')}</p>
          <p className="mt-1 text-2xl font-bold text-red-600">{data.gap_count}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={gapsOnly}
            onChange={(e) => setGapsOnly(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          {t('analytics.showGapsOnly', 'Show gaps only')}
        </label>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">{t('analytics.skill', 'Skill')}</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">{t('analytics.category', 'Category')}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">{t('analytics.demandedBy', 'Demanded By')}</th>
              <th className="px-4 py-3 text-center font-medium text-gray-500">{t('analytics.available', 'Available')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {displayed.map((gap) => (
              <tr key={gap.skill_name} className={gap.any_employee_has ? '' : 'bg-red-50'}>
                <td className="px-4 py-3 font-medium text-gray-900">{gap.skill_name}</td>
                <td className="px-4 py-3 text-gray-500">{gap.category}</td>
                <td className="px-4 py-3 text-right text-gray-700">
                  {gap.tender_demand_count} {t('analytics.tenders', 'tenders')}
                </td>
                <td className="px-4 py-3 text-center">
                  {gap.any_employee_has ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700" aria-label={t('analytics.yes', 'Yes')}>
                      ✓
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" aria-label={t('analytics.no', 'No')}>
                      ✗
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 2: Tender Personas
// -------------------------------------------------------------------------

function TenderPersonasTab() {
  const { t } = useTranslation();
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [selectedTenderId, setSelectedTenderId] = useState<string>('');
  const [data, setData] = useState<TenderPersonaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTenders({ page: 1, page_size: 100 })
      .then((result) => { if (!cancelled) setTenders(result.items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingTenders(false); });
    return () => { cancelled = true; };
  }, []);

  const loadPersonas = useCallback((tenderId: string) => {
    if (!tenderId) return;
    setSelectedTenderId(tenderId);
    setLoading(true);
    setError(null);
    fetchTenderPersonas(tenderId)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loadingTenders) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Tender selector */}
      <div className="max-w-md">
        <label id="tender-select-label" className="block text-sm font-medium text-gray-700 mb-1">
          {t('analytics.selectTender', 'Select a tender')}
        </label>
        <Select
          aria-labelledby="tender-select-label"
          options={tenders.map((t) => ({ value: t.id, label: `${t.title} (${t.client})` }))}
          value={selectedTenderId ? { value: selectedTenderId, label: tenders.find((t) => t.id === selectedTenderId)?.title ?? '' } : null}
          onChange={(opt) => loadPersonas(opt?.value ?? '')}
          isClearable
          placeholder={t('analytics.chooseTender', '-- Choose a tender --')}
          classNamePrefix="react-select"
          styles={{
            control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
            option: (base) => ({ ...base, fontSize: '0.875rem' }),
          }}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      {data && !loading && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('analytics.personasFound', '{{count}} personas identified for', { count: data.personas.length })}{' '}
            <span className="font-semibold">{data.tender_title}</span>
          </p>
          {data.personas.length === 0 ? (
            <EmptyState message={t('analytics.noPersonas', 'No personas identified for this tender.')} />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {data.personas.map((persona, i) => (
                <PersonaCard
                  key={persona.persona_id ?? `custom-${i}`}
                  personaName={persona.persona_name}
                  description={persona.description}
                  requiredSkills={persona.required_skills}
                  reason={persona.reason}
                  isCustom={persona.custom}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 3: Persona Matches
// -------------------------------------------------------------------------

function PersonaMatchesTab() {
  const { t } = useTranslation();
  const [data, setData] = useState<PersonaMatchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [minScore, setMinScore] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchPersonaMatches()
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <EmptyState message={t('analytics.noPersonaMatches', 'No persona match data available.')} />;

  return (
    <div className="space-y-4">
      {/* Min score filter */}
      <div className="flex items-center gap-3">
        <label htmlFor="min-score-filter" className="text-sm text-gray-600">
          {t('analytics.minFitScore', 'Min fit score')}:
        </label>
        <input
          id="min-score-filter"
          type="range"
          min={0}
          max={100}
          step={5}
          value={minScore}
          onChange={(e) => setMinScore(Number(e.target.value))}
          className="w-48"
        />
        <span className="text-sm font-medium text-gray-700">{minScore}%</span>
      </div>

      <div className="space-y-4">
        {data.map((pm) => {
          const filtered = pm.employees.filter((e) => Math.round(e.fit_score * 100) >= minScore);
          return (
            <PersonaCard
              key={pm.persona_id}
              personaName={pm.persona_name}
              description={`${filtered.length} ${t('analytics.matchingEmployeesCount', 'matching employees')}`}
              requiredSkills={[]}
              employees={filtered}
              expanded={expandedId === pm.persona_id}
              onToggle={() => setExpandedId(expandedId === pm.persona_id ? null : pm.persona_id)}
            />
          );
        })}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 4: Employee Gaps
// -------------------------------------------------------------------------

function EmployeeGapsTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [data, setData] = useState<EmployeePersonaGapResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEmployees({ page: 1, page_size: 200, status: 'ACTIVE' })
      .then((result) => { if (!cancelled) setEmployees(result.items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, []);

  const loadGaps = useCallback((employeeId: string) => {
    if (!employeeId) return;
    setSelectedEmployeeId(employeeId);
    setLoading(true);
    setError(null);
    fetchEmployeePersonaGaps(employeeId)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loadingEmployees) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Employee selector */}
      <div className="max-w-md">
        <label id="analytics-employee-label" className="block text-sm font-medium text-gray-700 mb-1">
          {t('analytics.selectEmployee', 'Select an employee')}
        </label>
        <Select
          aria-labelledby="analytics-employee-label"
          options={employees.map((emp) => ({ value: emp.id, label: `${emp.full_name} (${emp.department ?? '—'})` }))}
          value={selectedEmployeeId ? { value: selectedEmployeeId, label: employees.find((e) => e.id === selectedEmployeeId)?.full_name ?? '' } : null}
          onChange={(opt) => loadGaps(opt?.value ?? '')}
          isClearable
          placeholder={t('analytics.chooseEmployee', '-- Choose an employee --')}
          classNamePrefix="react-select"
          styles={{
            control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
            option: (base) => ({ ...base, fontSize: '0.875rem' }),
          }}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      {data && !loading && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {data.employee_name}
          </h2>

          {data.persona_gaps.length === 0 ? (
            <EmptyState message={t('analytics.noPersonaGaps', 'No persona gap data for this employee.')} />
          ) : (
            <div className="space-y-3">
              {data.persona_gaps.map((gap, index) => {
                const pct = Math.round(gap.fit_score * 100);
                const isTop = index === 0;
                return (
                  <div
                    key={gap.persona_id}
                    className={[
                      'rounded-xl border p-4',
                      isTop ? 'border-brand-300 bg-brand-50' : 'border-gray-200 bg-white',
                    ].join(' ')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{gap.persona_name}</h3>
                        {isTop && (
                          <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                            {t('analytics.bestMatch', 'Best Match')}
                          </span>
                        )}
                      </div>
                      <span className={[
                        'rounded-full px-3 py-1 text-sm font-semibold',
                        pct >= 80 ? 'bg-green-100 text-green-700' :
                        pct >= 60 ? 'bg-blue-100 text-blue-700' :
                        pct >= 40 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700',
                      ].join(' ')}>
                        {pct}%
                      </span>
                    </div>

                    {/* Score bar */}
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200" aria-hidden="true">
                      <div
                        className={[
                          'h-2 rounded-full transition-all',
                          pct >= 80 ? 'bg-green-500' :
                          pct >= 60 ? 'bg-blue-500' :
                          pct >= 40 ? 'bg-amber-500' :
                          'bg-red-500',
                        ].join(' ')}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {gap.matched_skills.map((s) => (
                        <SkillBadge key={s} skill={s} matched />
                      ))}
                      {gap.missing_skills.map((s) => (
                        <SkillBadge key={s} skill={s} matched={false} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Shared utility components
// -------------------------------------------------------------------------

function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-12" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
      {t('common.error', 'Error')}: {message}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
      {message}
    </div>
  );
}
